/**
 * PLANO DE CARGOS E SALÁRIOS 2026 — o que a tela precisa saber do plano
 * (servidor: server/plano-cargos.ts).
 *
 * As contas da Base Vigente são as MESMAS fórmulas da planilha:
 *   Qtd. vigente  = COUNTIF(Base!Cargo, cargo)
 *   Mínimo/Máximo = MINIFS/MAXIFS(Base!Salário, Base!Cargo, cargo)
 *   Médio         = AVERAGEIF(Base!Cargo, cargo, Base!Salário)
 *   Situação      = sem ocupante → "Faixa a validar"; mínimo = máximo →
 *                   "Salário único vigente"; senão "Há dispersão salarial".
 * (Na planilha, MINIFS/MAXIFS aparecem como #NOME? em versões do Excel sem
 * essas funções — aqui a conta é feita de verdade.)
 */
import type { Field, Row } from "./types";

/** Compara ignorando acento, caixa e espaços repetidos. */
export function norm(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export const num = (v: unknown): number | null =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v);

export type SituacaoSalarial = "Faixa a validar" | "Salário único vigente" | "Há dispersão salarial";

export interface Vigente {
  qtd: number;
  minimo: number | null;
  medio: number | null;
  maximo: number | null;
  situacao: SituacaoSalarial;
  /** As linhas da base que entraram na conta. */
  linhas: Row[];
}

/** Estatística vigente de UM cargo a partir da Base Vigente. */
export function vigenteDoCargo(cargo: unknown, base: Row[]): Vigente {
  const linhas = base.filter((b) => norm(b.cargo) === norm(cargo));
  const valores = linhas.map((b) => num(b.salario_base)).filter((v): v is number => v !== null);
  const qtd = linhas.length;
  const minimo = valores.length ? Math.min(...valores) : null;
  const maximo = valores.length ? Math.max(...valores) : null;
  const medio = valores.length ? valores.reduce((a, b) => a + b, 0) / valores.length : null;
  const situacao: SituacaoSalarial =
    qtd === 0 ? "Faixa a validar" : minimo === maximo ? "Salário único vigente" : "Há dispersão salarial";
  return { qtd, minimo, medio, maximo, situacao, linhas };
}

/** "+27,8%" — aumento da etapa sobre a anterior (conta da planilha). */
export function aumentoSobreAnterior(atual: unknown, anterior: unknown): string | null {
  const a = num(atual);
  const b = num(anterior);
  if (a === null || b === null || b === 0) return null;
  const pct = (a / b - 1) * 100;
  return `${pct >= 0 ? "+" : ""}${pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}

/** Data do banco (DATE) sem passar por fuso: "2026-03-10..." → "2026-03-10". */
export const dataIso = (v: unknown): string => String(v ?? "").slice(0, 10);

/** "2026-03-10" → "10/03/2026". */
export function dataBr(v: unknown): string {
  const [a, m, d] = dataIso(v).split("-");
  return a && m && d ? `${d}/${m}/${a}` : "—";
}

/** Textos de apoio que a planilha trazia no topo de cada aba. */
export const NOTAS_PLANO = {
  plano:
    "Base: somente colaboradores ativos da 1ª aba do arquivo 08.2026 Gestão de Colaboradores. Valores refletem salários-base praticados, sem pesquisa de mercado ou adicionais.",
  trilhas:
    "Salários sugeridos são referências internas com crescimento de complexidade entre etapas; devem ser validados com convenção coletiva, mercado, equidade e orçamento.",
  movimentacoes:
    "Preencher somente com documento comprobatório. As promoções registradas na aba Promoções entram aqui sozinhas.",
  painel:
    "Indicadores baseados exclusivamente em registros marcados como “Sim” em Movimentações.",
};

export const TIPOS_MOVIMENTACAO = ["Promoção", "Aumento salarial"];

/* ------------------------------------------------------------------------ */
/* Campos dos formulários (RecordForm) de cada parte do plano               */
/* ------------------------------------------------------------------------ */

const NIVEIS = [
  "Entrada", "Auxiliar", "Auxiliar/Assistente", "Assistente", "Operacional", "Técnico",
  "Júnior", "Pleno", "Analista", "Sênior", "Especialista", "Liderança", "Supervisão",
  "Coordenação", "Gerência",
];

export const CAMPOS_TRILHA: Field[] = [
  { name: "cargo_vigente", label: "Cargo vigente (dono da trilha)", type: "text", required: true },
  { name: "familia", label: "Família", type: "text", required: true },
  { name: "ordem", label: "Ordem", type: "number" },
  { name: "observacao", label: "Observação", type: "textarea" },
];

export const CAMPOS_ETAPA: Field[] = [
  { name: "etapa", label: "Etapa", type: "number", required: true },
  { name: "nivel", label: "Nível", type: "select", options: NIVEIS, required: true },
  { name: "cargo", label: "Cargo da trilha", type: "text", required: true },
  { name: "salario_sugerido", label: "Salário sugerido (R$)", type: "currency" },
  { name: "objetivo", label: "Objetivo do nível", type: "textarea" },
  { name: "atribuicoes", label: "Atribuições crescentes", type: "textarea" },
  { name: "formacao", label: "Formação", type: "textarea" },
  { name: "conhecimentos", label: "Conhecimentos", type: "textarea" },
  { name: "experiencia", label: "Experiência", type: "text" },
  { name: "criterio_avanco", label: "Critério de avanço", type: "textarea" },
];

export const CAMPOS_TEMA: Field[] = [
  { name: "titulo", label: "Tema", type: "text", required: true },
  { name: "ordem", label: "Ordem", type: "number" },
  { name: "texto", label: "Diretriz", type: "textarea", required: true },
];

export const CAMPOS_NIVEL: Field[] = [
  { name: "titulo", label: "Nível", type: "text", required: true },
  { name: "ordem", label: "Ordem", type: "number" },
  { name: "texto", label: "Autonomia esperada", type: "textarea", required: true },
  { name: "texto2", label: "Critério típico", type: "textarea", required: true },
];

/** O cargo da base é escolhido na lista do plano: o nome precisa bater. */
export function camposBase(cargos: string[]): Field[] {
  return [
    { name: "cargo", label: "Cargo padronizado", type: "select", options: cargos, required: true },
    { name: "cbo", label: "CBO informado", type: "text" },
    { name: "contrato", label: "Contrato", type: "select", options: ["SERCAMP", "MRN"], required: true },
    { name: "salario_base", label: "Salário-base vigente (R$)", type: "currency", required: true },
    { name: "ordem", label: "Ordem", type: "number" },
  ];
}

export function camposMovimentacao(setores: string[]): Field[] {
  return [
    { name: "data", label: "Data", type: "date", required: true },
    { name: "colaborador_id", label: "Colaborador", type: "select", ref: "colaboradores", required: true },
    { name: "tipo", label: "Tipo de movimentação", type: "select", options: TIPOS_MOVIMENTACAO, required: true },
    { name: "setor", label: "Setor", type: "select", options: setores, required: true },
    { name: "cargo_anterior", label: "Cargo anterior", type: "text" },
    { name: "cargo_novo", label: "Novo cargo", type: "cargo", preencheSalario: "salario_novo" },
    { name: "salario_anterior", label: "Salário anterior (R$)", type: "currency" },
    { name: "salario_novo", label: "Novo salário (R$)", type: "currency" },
    { name: "confirmado", label: "Confirmado?", type: "select", options: ["Sim", "Não"], padrao: "Não", required: true },
    { name: "documento", label: "Documento / evidência", type: "textarea" },
  ];
}
